import {ForeignKey, Model, isEmpty} from "weed-js";
import {Organization} from "./sso.js";
import {
    CanonicalIntegrationAccount,
    CanonicalBrand,
    CanonicalCategory,
    CanonicalEmployee,
    CanonicalFacility,
    CanonicalInventory,
    CanonicalMasterCategory,
    CanonicalPackage,
    CanonicalProduct,
    CanonicalStrain,
    CanonicalTransaction,
    CanonicalTransactionItem,
    CanonicalVendor,
    CanonicalDeliveredPackage,
} from "./canonical";
import {METRC} from "../constants";


export class MetrcModel extends Model {
    static namespace = METRC;
    static integration = METRC;
    static dal = (client) => {
        return client.metrc.dal.v1();
    }

    static getParams({startDate, endDate, metrc_account_id__in, selectedIntegrationAccounts}) {
        console.log("MetrcModel getParams called with ", {startDate, endDate, metrc_account_id__in, selectedIntegrationAccounts});
        if (!isEmpty(selectedIntegrationAccounts) && selectedIntegrationAccounts.length > 0) {
            return {
                metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
            }
        }
        if (isEmpty(metrc_account_id__in) || metrc_account_id__in.length === 0) {
            return {};
        }
        return {
            metrc_account_id__in: metrc_account_id__in
        }
    }
}

export class MetrcAccount extends MetrcModel {
    static columns = ["id", "display_name", "organization_id"];
    static pluralName = "metrc_accounts";
    static uri = "/metrc-accounts/";
    static canonicalClasses = [CanonicalIntegrationAccount];

    static dal = (client) => {
        return client.sso.dal.v1();
    }

    static foreignKeys = {
        organization: new ForeignKey(Organization)
    }
}

export class Facility extends MetrcModel {

    static columns = [
        "id",
        "metrc_account_id",
        "license_number",
        "name",
    ];

    static uri = "/facilities/";
    static pluralName = "facilities";

    static foreignKeys = {
        metrc_account: new ForeignKey(MetrcAccount)
    }

    static canonicalClasses = [CanonicalFacility, CanonicalVendor];
}

export class ProductCategoryType extends MetrcModel {
    static columns = [
        "id",
        "name",
        "metrc_account_id",
    ];
    static uri = "/product-category-types/";
    static pluralName = "product_category_types";
    static canonicalClasses = [CanonicalMasterCategory];
    static productIdField = "product_categories__products__id";
    static dateField = "product_categories__products__transactions__receipt__sales_date_time";
}

export class ProductCategory extends MetrcModel {
    static columns = [
        "id",
        "name",
        "metrc_account_id",
        "type_id",
    ];
    static uri = "/product-categories/";
    static pluralName = "product_categories";
    static foreignKeys = {
        type: new ForeignKey(ProductCategoryType)
    };
    static canonicalClasses = [CanonicalCategory];
    static productIdField = "products__id";
}

export class Product extends MetrcModel {
    static columns = [
        "id",
        "name",
        "metrc_account_id",
        "category_id",
    ];
    static uri = "/products/";
    static dateField = "transactions__receipt__sales_date_time";
    static pluralName = "products";
    static foreignKeys = {
        category: new ForeignKey(ProductCategory)
    };
    static canonicalClasses = [CanonicalProduct];
    static productIdField = "id";

    static getParams({startDate, endDate, metrc_account_id__in, selectedIntegrationAccounts}) {
        if (!isEmpty(selectedIntegrationAccounts) && selectedIntegrationAccounts.length > 0) {
            return {
                transactions__receipt__metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
            }
        }

        return {
            transactions__receipt__metrc_account_id__in: metrc_account_id__in,
        }
    }
}

export class LocationType extends MetrcModel {
    static columns = [
        "id",
        "name",
        "metrc_account_id",
    ];
    static uri = "/location-types/";
    static pluralName = "location_types";
}

export class Location extends MetrcModel {
    static columns = [
        "id",
        "metrc_account_id",
        "facility_id",
        "external_id",
        "name",
        "type_id",
    ];
    static uri = "/locations/";
    static pluralName = "locations";
    static foreignKeys = {
        facility: new ForeignKey(Facility),
        type: new ForeignKey(LocationType)
    };
}

export class Package extends MetrcModel {
    static columns = [
        "id",
        "facility_id",
        "location_id",
        "status_id",
        "type_id",
        "external_id",
        "expiration_date",
        "label",
        "last_modified",
        "metrc_account_id",
        "packaged_date",
        "quantity",
        "sell_by_date",
        "use_by_date",
    ];
    static uri = "/packages/";
    static dateField = "transactions__receipt__sales_date_time";
    static pluralName = "packages";
    static foreignKeys = {
        facility: new ForeignKey(Facility),
        location: new ForeignKey(Location)
    };
    static productIdField = "items__product__id";
    static canonicalClasses = [CanonicalPackage];

    static getParams({startDate, endDate, metrc_account_id__in, selectedIntegrationAccounts}) {
        if (!isEmpty(selectedIntegrationAccounts) && selectedIntegrationAccounts.length > 0) {
            return {
                transactions__receipt__metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
            }
        }
        return {
            transactions__receipt__metrc_account_id__in: metrc_account_id__in,
        }
    }
}

export class Employee extends MetrcModel {
    static uri = "/employees/";
    static columns = [
        "id",
        "metrc_account_id",
        "name"
    ];
    static canonicalClasses = [CanonicalEmployee];
    static dateField = "transactions__receipt__sales_date_time";
    static pluralName = "employees";
    static foreignKeys = {
        metrc_account: new ForeignKey(MetrcAccount)
    };
}

export class Receipt extends MetrcModel {
    static uri = "/receipts/";
    static columns = [
        "id",
        "metrc_account_id",
        "recorded_by_id",
        "facility_id",
        "status_id",
        "external_id",
        "sales_date_time",
        "last_modified"
    ];
    static dateField = "sales_date_time";
    static pluralName = "receipts";
    static foreignKeys = {
        facility: new ForeignKey(Facility),
        recorded_by: new ForeignKey(Employee),
        metrc_account: new ForeignKey(MetrcAccount)
    }
    static productIdField = "transactions__products__id";

}

export class Tax extends MetrcModel {
    static uri = "/taxes/";
    static columns = [
        "id", "city", "county", "excise", "municipal", "sales"
    ]
    static pluralName = "taxes";
    static dateField = "transactions__receipt__sales_date_time";
    static productIdField = "transactions__package__items__product__id";
}


export class Transaction extends MetrcModel {
    static columns = [
        "id",
        "metrc_account_id",
        "sub_total",
        "tax_id",
        "total_price",
        "discount_amount",
        "recorded_date_time",
        "facility_id",
        "package_id",
        "price",
        "quantity_sold",
        "receipt_id",
    ];
    static uri = "/transactions/";
    static dateField = "receipt__sales_date_time";
    static pluralName = "transactions";
    static foreignKeys = {
        facility: new ForeignKey(Facility),
        package: new ForeignKey(Package),
        receipt: new ForeignKey(Receipt),
        metrc_account: new ForeignKey(MetrcAccount),
        tax: new ForeignKey(Tax),
    };
    static canonicalClasses = [CanonicalTransaction];
    static productIdField = "package__items__product__id";

    static getParams({startDate, endDate, metrc_account_id__in, selectedIntegrationAccounts}) {
        if (!isEmpty(selectedIntegrationAccounts) && selectedIntegrationAccounts.length > 0) {
            return {
                receipt__metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
            }
        }
        return {
            receipt__metrc_account_id__in: metrc_account_id__in,
        }
    }

}


export class Transfer extends MetrcModel {

    static uri = "/transfers/";
    static pluralName = "transfers";
    static columns = [
        "id",
        "metrc_account_id",
        "shipper_facility_id",
        "recipient_facility_id",
        "external_id",
        "last_modified",
        "created_date_time",
    ];
    static dateField = "delivery__delivered_packages__package__transactions__receipt__sales_date_time";
    static foreignKeys = {
        shipper_facility: new ForeignKey(Facility),
        recipient_facility: new ForeignKey(Facility),
        metrc_account: new ForeignKey(MetrcAccount),
    }
    static getParams({startDate, endDate, metrc_account_id__in, selectedIntegrationAccounts}) {
        if (!isEmpty(selectedIntegrationAccounts) && selectedIntegrationAccounts.length > 0) {
            return {
                delivery__delivered_packages__package__transactions__receipt__metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
            }
        }

        return {
            delivery__delivered_packages__package__transactions__receipt__metrc_account_id__in: metrc_account_id__in,
        }
    }
}


export class Delivery extends MetrcModel {
    static uri = "/deliveries/";
    static pluralName = "deliveries";
    static columns = [
        "id", "metrc_account_id", "transfer_id", "recipient_facility_id"
    ]

    static foreignKeys = {
        transfer: new ForeignKey(Transfer),
        shipper_facility: new ForeignKey(Facility),
        recipient_facility: new ForeignKey(Facility),
        metrc_account: new ForeignKey(MetrcAccount),
    }

    static dateField = "delivered_packages__package__transactions__receipt__sales_date_time";

    static getParams({startDate, endDate, metrc_account_id__in, selectedIntegrationAccounts}) {
        if (!isEmpty(selectedIntegrationAccounts) && selectedIntegrationAccounts.length > 0) {
            return {
                delivered_packages__package__transactions__receipt__metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
            }
        }
        return {
            delivered_packages__package__transactions__receipt__metrc_account_id__in: metrc_account_id__in,
        }
    }

}

export class DeliveredPackage extends MetrcModel {
    static pluralName = "delivered_packages";
    static uri = "/delivered-packages/";
    static columns = [
        "id",
        "delivery_id",
        "package_id",
        "metrc_account_id",
        "received_quantity",
        "shipped_quantity",
    ];

    static foreignKeys = {
        delivery: new ForeignKey(Delivery),
        package: new ForeignKey(Package),
        metrc_account: new ForeignKey(MetrcAccount),
    }

    static canonicalClasses = [CanonicalDeliveredPackage];
    static dateField = "package__transactions__receipt__sales_date_time";
    static getParams({startDate, endDate, metrc_account_id__in, selectedIntegrationAccounts}) {
        if (!isEmpty(selectedIntegrationAccounts) && selectedIntegrationAccounts.length > 0) {
            return {
                package__transactions__receipt__metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
            }
        }
        return {
            package__transactions__receipt__metrc_account_id__in: metrc_account_id__in,
        }
    }
}

export class Strain extends MetrcModel {
    static uri = "/strains/";
    static columns = [
        "id",
        "name",
        "metrc_account_id",
        "thc_level",
        "cbd_level",
        "indica_percentage",
        "sativa_percentage",
    ];
    static dateField = "transaction_items__transaction__receipt__sales_date_time";

    static canonicalClasses = [CanonicalStrain];
    static getParams({startDate, endDate, metrc_account_id__in, selectedIntegrationAccounts}) {
        if (!isEmpty(selectedIntegrationAccounts) && selectedIntegrationAccounts.length > 0) {
            return {
                transaction_items__transaction__receipt__metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
            }
        }
        return {
            transaction_items__transaction__receipt__metrc_account_id__in: metrc_account_id__in,
        }
    }
}

export class Brand extends MetrcModel {
    static uri = "/brands/";
    static columns = [
        "id",
        "name",
        "metrc_account_id",
    ];
    static pluralName = "brands";
    static canonicalClasses = [CanonicalBrand];
    static dateField = "items__package__transactions__receipt__sales_date_time";

}

export class PackageWholesalePrice extends MetrcModel {
    static uri = "/package-wholesale-prices/";
    static columns = [
        "id",
        "package_id",
        "delivery_id",
        "metrc_account_id",
        "shipper_wholesale_price",
        "receiver_wholesale_price",
    ]
    static dateField = "package__transactions__receipt__sales_date_time";
    static pluralName = "package_wholesale_prices";

    static foreignKeys = {
        package: new ForeignKey(Package),
    }

    static getParams({startDate, endDate, metrc_account_id__in, selectedIntegrationAccounts}) {
        if (!isEmpty(selectedIntegrationAccounts) && selectedIntegrationAccounts.length > 0) {
            return {
                package__transactions__receipt__metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
            }
        }
        return {
            package__transactions__receipt__metrc_account_id__in: metrc_account_id__in,
        }
    }
}

export class InventoryItem extends MetrcModel {
    static uri = "/items/";
    static columns = [
        "id",
        "metrc_account_id",
        "brand_id",
        "category_id",
        "strain_id",
        "unit_of_measure_id",
        "package_id",
        "product_id",
        "description",
        "external_id",
        "name",
        "unit_quantity",
        "unit_weight",
        "unit_volume",
        "number_of_doses",
        "serving_size"
    ];
    static dateField = "package__transactions__receipt__sales_date_time";
    static pluralName = "inventory_items";
    static foreignKeys = {
        strain: new ForeignKey(Strain),
        product: new ForeignKey(Product),
        package: new ForeignKey(Package),
        category: new ForeignKey(ProductCategory),
        brand: new ForeignKey(Brand),
    }
    static canonicalClasses = [CanonicalInventory];
    static productIdField = "product_id";
    static getParams({startDate, endDate, metrc_account_id__in, selectedIntegrationAccounts}) {
        if (!isEmpty(selectedIntegrationAccounts) && selectedIntegrationAccounts.length > 0) {
            return {
                package__transactions__receipt__metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
            }
        }
        return {

            package__transactions__receipt__metrc_account_id__in: metrc_account_id__in,
        }
    }
}

export class Item extends MetrcModel {
    static uri = "/transaction-items/";
    static columns = [
        "id",
        "metrc_account_id",
        "transaction_id",
    ];
    static dateField = "transaction__receipt__sales_date_time";
    static pluralName = "items";
    static foreignKeys = {
        transaction: new ForeignKey(Transaction),
        metrc_account: new ForeignKey(MetrcAccount)
    };
    static canonicalClasses = [CanonicalTransactionItem];
    static productIdField = "transaction__package__items__product__id";

    toRow() {
        if (isEmpty(this.transaction)) {
            console.error("Transaction not loaded for item ", this);
            throw new Error("Transaction not loaded for item " + this.id);
        }
        if (isEmpty(this.transaction.receipt)) {
            console.error("Receipt not loaded for item ", this);
            throw new Error("Receipt not loaded for item "+ this.id);
        }
        if (isEmpty(this.transaction.package)) {
            console.error("Package not loaded for item ", this);
            throw new Error("Package not loaded for item "+ this.id);
        }
        if (isEmpty(this.transaction.package.inventory_items) || this.transaction.package.inventory_items.size === 0) {
            console.error("Inventory items not loaded for item ", this);
            throw new Error("Inventory items not loaded for item "+ this.id);
        }

        const inventoryItems = Array.from(this.transaction.package.inventory_items);
        if (isEmpty(inventoryItems) || inventoryItems.length === 0) {
            console.error("Inventory items not loaded for item ", this);
            throw new Error("Inventory items not loaded for item "+ this.id);
        }
        const inventoryItem = inventoryItems.find(i => !isEmpty(i.product));
        const product = inventoryItem.product;
        const brand = inventoryItems.find(ii => !isEmpty(ii.brand_id))?.brand;
        const transaction = this.transaction;
        const receipt = transaction?.receipt;
        const facility = receipt?.facility;

        const metrcPackage = this.transaction?.package;

        if (isEmpty(facility)) {
            console.error("Facility not loaded for item ", this);
            throw new Error("Facility not loaded for item "+ this.id);
        }
        if (isEmpty(product)) {
            console.error("Product not loaded for item ", this);
            throw new Error("Product not loaded for item "+ this.id);
        }
        if (isEmpty(product.category)) {
            console.error("Category not loaded for product ", product);
            throw new Error("Category not loaded for item "+ this.id);
        }
        if (!isEmpty(inventoryItem.brand_id) && isEmpty(brand)) {
            console.error("Brand not loaded for inventory item ", inventoryItem, inventoryItems);
            throw new Error("Brand not loaded for item "+ this.id);
        }

        const packageWholesalePrices = Array.from(this.transaction?.package?.package_wholesale_prices || []);
        const packageWholesalePrice = packageWholesalePrices.length > 0 ? packageWholesalePrices.find((pwp) => {
            if (pwp.shipper_wholesale_price !== null && pwp.shipper_wholesale_price !== undefined) {
                return true;
            }
            if (pwp.receiver_wholesale_price !== null && pwp.receiver_wholesale_price !== undefined) {
                return true;
            }
            return false;
        }) : null;
        const quantityAvailable = metrcPackage?.quantity !== null && metrcPackage?.quantity !== undefined ? metrcPackage?.quantity : null;
        const quantitySold = this.transaction?.quantity_sold !== null && this.transaction?.quantity_sold !== undefined ? this.transaction?.quantity_sold : null;
        const totalPrice = this.transaction?.total_price !== null && this.transaction?.total_price !== undefined ? this.transaction?.total_price : null;
        const unitPrice = (quantitySold !== null && totalPrice !== null) ? (totalPrice / quantitySold) : null;
        const receiverWholesalePrice = packageWholesalePrice?.receiver_wholesale_price !== null && packageWholesalePrice?.receiver_wholesale_price !== undefined ? packageWholesalePrice?.receiver_wholesale_price : null;
        const shipperWholesalePrice = packageWholesalePrice?.shipper_wholesale_price !== null && packageWholesalePrice?.shipper_wholesale_price !== undefined ? packageWholesalePrice?.shipper_wholesale_price : null;
        const wholesalePrice = (receiverWholesalePrice !== null) ? receiverWholesalePrice : (shipperWholesalePrice !== null ? shipperWholesalePrice : null);
        const totalProfit = (wholesalePrice !== null && totalPrice !== null) ? (totalPrice - wholesalePrice) : null;
        const unitCost = (quantitySold !== null && wholesalePrice !== null) ? (wholesalePrice / quantitySold) : null;

        if (isNaN(parseFloat(totalPrice))) {
            console.warn("Total price is NaN for item ", this);
        }
        // TODO: Need organization id
        return [
            this.transaction.receipt.external_id,
            this.transaction.id,
            this.transaction.receipt.recorded_by.external_id,
            facility.name,
            product.category.type.name,
            product.category.name,
            brand?.name,
            "---",
            product?.id > -1 ? {
                props: {
                    href: `/analytics/organizations/${facility.metrc_account.organization_id}/integration/metrc/product/${product?.id}`,
                    role: "link"
                },
                displayValue: product.name,
            } : null,
            "---", // SKU
            quantityAvailable,
            quantitySold,
            totalPrice,
            !isNaN(quantitySold) && !isNaN(unitCost) ? quantitySold * unitCost : null, // Total Cost
            totalProfit,
            unitPrice,
            unitCost,
            this.transaction.receipt.sales_date_time
        ]
    }
}
